import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import SalonService from "../../services/SalonService";
import moment from "moment";
import "./ViewBookings.css";
import { toast } from "react-toastify";
import { useSalon } from "../../context/SalonContext";
import GlobalLoader from "../loadingIndicator/GlobalLoader";
import "../../style.css";
import {
    FaCalendarAlt, FaClock, FaCut, FaCheckCircle,
    FaTimesCircle, FaHourglassHalf, FaChevronRight, FaCreditCard,
    FaInfoCircle, FaUndoAlt, FaExclamationCircle
} from "react-icons/fa";

const ViewBookings = () => {
    const { customerId, salonName } = useSalon();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [dataSource, setDataSource] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const today = moment().format('YYYY-MM-DD');
    const [cancellationReason, setCancellationReason] = useState("");
    const [showCancelId, setShowCancelId] = useState(null);

    useEffect(() => {
        fetchAppointments(0);
    }, []);

    const fetchAppointments = async (page = 0) => {
        setLoading(true);
        const year = new Date().getFullYear();
        try {
            const response = await SalonService.getUserAppointments(customerId, year, page);
            const content = response?.data?.content || [];

            if (page === 0) {
                setDataSource(content);
            } else {
                setDataSource(prev => [...prev, ...content]);
            }

            if (content.length === 0 || content.length < 10) {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error fetching appointments:", error);
            toast.error("Failed to load appointments");
        } finally {
            setLoading(false);
        }
    };

    const loadMore = () => {
        if (loading || !hasMore) return;
        const nextPage = offset + 1;
        setOffset(nextPage);
        fetchAppointments(nextPage);
    };

    const cancellationAllowed = (item) => {
        const currentDate = new Date();
        const timeInMinutes = (currentDate.getHours() * 60) + currentDate.getMinutes();
        const isFutureDate = item.apntDate > today;
        const isTodayWithGap = item.apntDate === today && timeInMinutes < item.apntStart - 60;

        return item.apntStatus === 'BOOKED' && (isFutureDate || isTodayWithGap);
    };

    const cancelAppointment = async (apntId) => {
        if (!cancellationReason.trim()) {
            toast.warn("Please provide a reason for cancellation");
            return;
        }

        setLoading(true);
        try {
            const response = await SalonService.cancelAppointment({ apntId, reason: cancellationReason });
            if (response.data) {
                toast.success('Appointment cancelled successfully');
                setDataSource(prev => prev.map(item =>
                    item.apntId === apntId ? { ...item, apntStatus: 'CANCELLED' } : item
                ));
                setShowCancelId(null);
                setCancellationReason("");
            } else {
                toast.error('Cancellation failed. Please contact salon.');
            }
        } catch (error) {
            toast.error('Cancellation failed. Please contact salon.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'BOOKED': return <FaCheckCircle className="status-icon booked" />;
            case 'CANCELLED': return <FaTimesCircle className="status-icon cancelled" />;
            case 'EXPIRED': return <FaHourglassHalf className="status-icon expired" />;
            default: return <FaInfoCircle className="status-icon" />;
        }
    };

    const PaymentInfo = ({ item }) => {
        if (item.paymentStatus === "NOT_REQUIRED" || !item.advanceAmount) {
            return (
                <div className="payment-tag not-required">
                    <FaCreditCard className="me-1" /> Pay at Salon
                </div>
            );
        }

        const isPaid = ["PAID", "VERIFIED"].includes(item.paymentStatus);
        const isSubmitted = item.paymentStatus === "SUBMITTED";

        if (isPaid) {
            return (
                <div className="payment-tag paid">
                    <FaCheckCircle className="me-1" /> Advance Paid ₹{item.advanceAmount}
                </div>
            );
        }

        if (isSubmitted) {
            return (
                <div className="payment-tag pending">
                    <FaHourglassHalf className="me-1" /> Verification Pending
                </div>
            );
        }

        if (item.apntStatus === "BOOKED") {
            return (
                <button
                    className="premium-pay-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/pay/${item.bookingRef}`, {
                            state: {
                                customerId,
                                salonUuid: item.salonUuid,
                                bookingRef: item.bookingRef,
                                amount: item.advanceAmount,
                            },
                        });
                    }}
                >
                    <FaCreditCard className="me-1" /> Pay Advance ₹{item.advanceAmount}
                </button>
            );
        }

        return null;
    };

    return (
        <div className="view-bookings-page">
            <div className="appointments-content-container py-4">
                {loading && dataSource.length === 0 && <GlobalLoader />}

                {!loading && dataSource.length === 0 && (
                    <div className="empty-state-bookings">
                        <div className="empty-icon">📅</div>
                        <h3>No Appointments Found</h3>
                        <p>You haven't booked any appointments yet.</p>
                        <button className="btn btn-primary rounded-pill px-4 mt-3" onClick={() => navigate('/booking')}>
                            Book Now
                        </button>
                    </div>
                )}

                <div className="bookings-list">
                    {dataSource.map((item) => (
                        <div
                            key={item.apntId}
                            className={`premium-booking-card ${moment(item.apntDate).isBefore(today) ? 'past' : ''}`}
                            onClick={() => navigate(`/appointment/${item.apntId}`, {
                                state: {
                                    apntId: item.apntId,
                                    salonUuid: item.salonUuid
                                }
                            })}
                        >
                            <div className="booking-card-header">
                                <div className="salon-main-info">
                                    <h3 className="salon-title-text">{item.salonName}</h3>
                                    <div className={`status-pill ${item.apntStatus.toLowerCase()}`}>
                                        {getStatusIcon(item.apntStatus)}
                                        {item.apntStatus}
                                    </div>
                                </div>
                                <FaChevronRight className="arrow-icon" />
                            </div>

                            <div className="booking-card-body">
                                <div className="booking-meta-grid">
                                    <div className="meta-item">
                                        <FaCalendarAlt className="meta-icon" />
                                        <div className="meta-content">
                                            <span className="meta-label">Date</span>
                                            <span className="meta-value">{moment(item.apntDate).format("DD MMM, YYYY")}</span>
                                        </div>
                                    </div>
                                    <div className="meta-item">
                                        <FaClock className="meta-icon" />
                                        <div className="meta-content">
                                            <span className="meta-label">Time</span>
                                            <span className="meta-value">{item.apntTime}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="service-info-row">
                                    <FaCut className="meta-icon" />
                                    <div className="meta-content">
                                        <span className="meta-label">Services</span>
                                        <span className="meta-value truncate-text">{item.serviceNames}</span>
                                    </div>
                                </div>

                                {item.apntStatus === "BOOKED" && item.verificationNumber && item.verificationNumber !== "null" && (
                                    <div className="verification-code-box">
                                        <span className="code-label">Verification Code</span>
                                        <span className="code-value">{item.verificationNumber}</span>
                                    </div>
                                )}

                                <div className="booking-card-footer">
                                    <PaymentInfo item={item} />

                                    {cancellationAllowed(item) && (
                                        <button
                                            className="cancel-trigger-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowCancelId(showCancelId === item.apntId ? null : item.apntId);
                                            }}
                                        >
                                            <FaExclamationCircle className="me-1" />
                                            {showCancelId === item.apntId ? "Close" : "Cancel"}
                                        </button>
                                    )}
                                </div>

                                {showCancelId === item.apntId && (
                                    <div className="cancellation-form" onClick={(e) => e.stopPropagation()}>
                                        <textarea
                                            placeholder="Why are you cancelling?"
                                            value={cancellationReason}
                                            onChange={(e) => setCancellationReason(e.target.value)}
                                            className="cancel-reason-input"
                                        />
                                        <button
                                            className="confirm-cancel-btn"
                                            onClick={() => cancelAppointment(item.apntId)}
                                        >
                                            <FaUndoAlt className="me-1" /> Confirm Cancellation
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {hasMore && (
                    <div className="load-more-section">
                        <button className="premium-load-more" onClick={loadMore} disabled={loading}>
                            {loading ? "Loading..." : "View Older Appointments"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewBookings;
